import {RoadKillNormalResultPresentation} from "../RoadKillNormalResultPresentation";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {TimelineLite} from "gsap";
import {RoadKill} from "../../RoadKill";
import {DIRECTION} from "../../component/enums/RoadKillEnums";
import {ISpecialSymData} from "../../server/RoadKillData";
import {RoadKillSound} from "../../sound/RoadKillSound";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {RoadKillGameMode} from "../../server/RoadKillGameMode";

export interface IMovingCarData {
    name: string,
    rowId: number,
    sourceReelId: number,
    destReelIds: number[],
    hitByAnimal?: string[],
    animalData?: ISpecialSymData[],
    direction: DIRECTION,
    hpLeft: number[],
}

export interface ICarAnimalData {
    name: string,
    rowId: number,
    sourceReelId: number,
    destReelId: number,
    animalData: ISpecialSymData,
    direction: DIRECTION,
    hpLeft: number,
}

export class RoadKillRespinResultPresentation extends RoadKillNormalResultPresentation {
    private playWildMoveSoundOnceFlag: boolean = true;
    private _mainTl: TimelineLite;

    public async start(flowStateController: FlowStateController) {
        const movingCarDetails: IMovingCarData[] = RoadKill.model.getMovingCarData(),
            mode: string = RoadKill.model.gameData.mode,
            nextMode: string = RoadKill.model.gameData.nextMode;
        const jwJumpTL: TimelineLite = new TimelineLite();
        this.playWildMoveSoundOnceFlag = true;
        const carMoveTl: TimelineLite = new TimelineLite();
        const carTeaseDuration: number = RoadKill.apiPlugin.settings.get(APISetting.FAST_SPIN, false) ? 0 : 1; //minimum 1 sec tease

        this._mainTl = new TimelineLite();
        this._mainTl.add(() => {
            flowStateController.setSkipCallback(() => this.skip());
        })
        this._mainTl.add(() => {
            RoadKill.soundPlayer.playTrack(RoadKillSound.HILLY_BILLY_POP_UP);
        }, 0)
        if (mode === RoadKillGameMode.FREESPIN) {
            if (!RoadKill.backgroundElements.hillyActive) {
                this._mainTl.add(RoadKill.backgroundElements.popInHilly(), 0);
            } else {
                this._mainTl.add(RoadKill.backgroundElements.activateHilly(), 0);
            }

            if (!RoadKill.backgroundElements.billyActive) {
                this._mainTl.add(RoadKill.backgroundElements.popInBilly()).progress(1).add(RoadKill.backgroundElements.billyIdleState(), 0).progress(1);
            } else {
                if (RoadKill.model.gameData.carInfo.length === 2) {
                    this._mainTl.add(RoadKill.backgroundElements.activateBilly(), 0);
                }
            }
        } else if (mode === RoadKillGameMode.SUPER_FREESPIN) {
            if (!RoadKill.backgroundElements.hillyActive) {
                this._mainTl.add(RoadKill.backgroundElements.popInHilly(), 0);
            } else {
                this._mainTl.add(RoadKill.backgroundElements.activateHilly());
            }

            if (!RoadKill.backgroundElements.billyActive) {
                this._mainTl.add(RoadKill.backgroundElements.popInBilly(), 0);
            } else {
                this._mainTl.add(RoadKill.backgroundElements.activateBilly(), 0);
            }
        } else if (mode === RoadKillGameMode.RESPIN) {
            if (!RoadKill.backgroundElements.hillyActive) {
                this._mainTl.add(RoadKill.backgroundElements.popInHilly(), 0);
            } else {
                this._mainTl.add(RoadKill.backgroundElements.activateHilly());
            }
        }

        this._mainTl.to({}, {duration: carTeaseDuration});
        RoadKill.model.gameData.specialSymbolList?.forEach((specSymData: ISpecialSymData) => {
            jwJumpTL.add([RoadKill.jumpingWildView.onResult(specSymData), this.playWildMoveSound(specSymData)], 0);
        });
        jwJumpTL.add(() => {
            RoadKill.reelView.modifyFakeReelSetToAddRemoveS1(RoadKill.model.gameData.evaluatedArea, RoadKill.model.mode, RoadKill.model.nextMode);
        })

        this._mainTl.add(jwJumpTL);
        this._mainTl.add(() => {
            RoadKill.movingCar.reset();
        });
        movingCarDetails.forEach((movingCarData: IMovingCarData, idx: number) => {
            if (movingCarData.name === "Red" && movingCarData.direction === DIRECTION.RIGHT) {
                carMoveTl.add(RoadKill.movingCar.setCarDirectionAndPos(DIRECTION.RIGHT, 1));
                carMoveTl.add(RoadKill.movingCar.playCarTease());
                carMoveTl.to(this, {duration: 2});
            }
            carMoveTl.add(RoadKill.movingCar.moveCar(movingCarData, idx));
        });
        if (nextMode === RoadKillGameMode.NORMAL) {
            carMoveTl.add([RoadKill.backgroundElements.popOutHilly(), RoadKill.backgroundElements.popOutBilly()]);
        } else {
            carMoveTl.add([RoadKill.backgroundElements.disableBillyTyres(0), RoadKill.backgroundElements.disableHilly(), () => {
                // RoadKill.soundPlayer.playTrack(RoadKillSound.HILLI_BILLY_REMOVE_TYRE);
            }]);
        }
        this._mainTl.add(carMoveTl);
        if (RoadKill.model.mode === RoadKillGameMode.SUPER_FREESPIN && RoadKill.model.totalAnimalHitsSFS === 20) {
            if (RoadKill.topAnimalController.totalHits !== 20) {
                this._mainTl.add(() => {
                    RoadKill.topAnimalController.setTotalHits(20)
                })
            }
        }
        if (RoadKill.model.mode === RoadKillGameMode.SUPER_FREESPIN && RoadKill.model.isMassiveWin && RoadKill.model.totalAnimalHitsSFS === 0) {
            RoadKill.topAnimalController.updateCombinedAnimalMultiToZero();
        }

        if (RoadKill.apiPlugin.settings.get(APISetting.FAST_SPIN, false)) {
            this._mainTl.timeScale(1.2);
        }
        await this._mainTl;
        super.start(flowStateController);
    }

    public skip(): void {
        this._mainTl.timeScale(2);
    }

    private playWildMoveSound(specSymData: ISpecialSymData): TimelineLite {
        return new TimelineLite().add(() => {
            if (this.playWildMoveSoundOnceFlag && !this.fromToCoordSame(specSymData)) {
                RoadKill.soundPlayer.playTrack(RoadKillSound.WILD_MOVE);
                this.playWildMoveSoundOnceFlag = false;
            }
        })
    }

    private fromToCoordSame(specSymData: ISpecialSymData): boolean {
        const to: number[] | undefined = specSymData.coordTo,
            from: number[] = specSymData.coordFrom;
        if (to) {
            if (to[0] === from[0] && to[1] === from[1]) {
                return true;
            }
        }
        return false;
    }
}